import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ThemeProvider, useTheme } from "./hooks/use-theme";
import { BusinessModeProvider } from "./hooks/use-business-mode";
import { lazy, Suspense, useEffect } from "react";
import ScrollToTop from "./components/ScrollToTop";
import WhatsAppOrb from "./components/WhatsAppOrb";
import CookieConsent from "./components/CookieConsent";
import Index from "./pages/Index";
const UeberUns = lazy(() => import("./pages/UeberUns"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const LocalSeoPage = lazy(() => import("./pages/LocalSeoPage"));
import { localSeoPages } from "./data/localSeoPages";
const Impressum = lazy(() => import("./pages/Impressum"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerrufsrecht = lazy(() => import("./pages/Widerrufsrecht"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Dienstleistungen = lazy(() => import("./pages/Dienstleistungen"));
const WarumWir = lazy(() => import("./pages/WarumWir"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ServicePage = lazy(() => import("./pages/services/ServicePage"));
const ShortServicePage = lazy(() => import("./pages/services/ShortServicePage"));
const LandingpageBeispiel = lazy(() => import("./pages/beispiele/LandingpageBeispiel"));
const BusinessBeispiel = lazy(() => import("./pages/beispiele/BusinessBeispiel"));
const OnlineShopBeispiel = lazy(() => import("./pages/beispiele/OnlineShopBeispiel"));
const WebAppBeispiel = lazy(() => import("./pages/beispiele/WebAppBeispiel"));
const TicketLogin = lazy(() => import("./pages/ticket-system/Login"));
const TicketResetPassword = lazy(() => import("./pages/ticket-system/ResetPassword"));
const TicketDashboard = lazy(() => import("./pages/ticket-system/Dashboard"));
const TicketOrdersList = lazy(() => import("./pages/ticket-system/OrdersList"));
const TicketOrderDetail = lazy(() => import("./pages/ticket-system/OrderDetail"));
const TicketCreateOrder = lazy(() => import("./pages/ticket-system/CreateOrder"));
const TicketTeam = lazy(() => import("./pages/ticket-system/TeamManagement"));
const TicketSettings = lazy(() => import("./pages/ticket-system/Settings"));
const TicketFinance = lazy(() => import("./pages/ticket-system/FinanceOverview"));
const AffiliatesAdmin = lazy(() => import("./pages/ticket-system/AffiliatesAdmin"));
const TicketCallList = lazy(() => import("./pages/ticket-system/CallList"));
const TicketSubcontractors = lazy(() => import("./pages/ticket-system/Subcontractors"));
const AffiliateLogin = lazy(() => import("./pages/affiliate/AffiliateLogin"));
const AffiliateDashboard = lazy(() => import("./pages/affiliate/AffiliateDashboard"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const BeratungsterminPage = lazy(() => import("./pages/BeratungsterminPage"));
const KostenHausmeisterservice = lazy(() => import("./pages/blog/KostenHausmeisterservice"));
const ServiceBlogPage = lazy(() => import("./pages/blog/ServiceBlogPage"));
const DigitalIT = lazy(() => import("./pages/DigitalIT"));
import ProtectedRoute from "./components/ticket-system/ProtectedRoute";

const queryClient = new QueryClient();

const ConditionalCookieConsent = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/ticket-system")) return null;
  return <CookieConsent />;
};

const ConditionalWhatsAppOrb = () => {
  const location = useLocation();
  if (location.pathname.startsWith("/ticket-system")) return null;
  return <WhatsAppOrb />;
};

const RouteThemeSync = () => {
  const location = useLocation();
  const { setTheme } = useTheme();
  useEffect(() => {
    const path = location.pathname;
    const isDarkRoute =
      path.startsWith("/ticket-system") ||
      path.startsWith("/affiliate") ||
      path.startsWith("/beispiele/") ||
      path.includes("website-erstellung") ||
      path.startsWith("/webentwicklung") ||
      path.startsWith("/digital-it") ||
      (path.startsWith("/beratungstermin") && location.search.includes("modus=digital"));
    setTheme(isDarkRoute ? "dark" : "light");
  }, [location.pathname, location.search, setTheme]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense key={location.pathname} fallback={<div className="min-h-screen bg-background" aria-busy="true" />}>
      <Routes location={location}>
        <Route path="/" element={<Index />} />
        <Route path="/ueber-uns" element={<UeberUns />} />
        <Route path="/kontakt" element={<Kontakt />} />
        <Route path="/dienstleistungen" element={<Dienstleistungen />} />
        <Route path="/digital-it" element={<DigitalIT />} />
        <Route path="/dienstleistungen/:slug" element={<ServicePage />} />
        {/* Short SEO-friendly canonical routes */}
        <Route path="/hausmeister" element={<ShortServicePage />} />
        <Route path="/elektrotechnik" element={<ShortServicePage />} />
        <Route path="/car-detailing" element={<ShortServicePage />} />
        <Route path="/it-helpdesk" element={<ShortServicePage />} />
        <Route path="/gartenarbeiten" element={<ShortServicePage />} />
        <Route path="/security-service" element={<ShortServicePage />} />
        <Route path="/winterdienst" element={<ShortServicePage />} />
        <Route path="/reinigungsservice" element={<ShortServicePage />} />
        <Route path="/webentwicklung" element={<ShortServicePage />} />
        <Route path="/impressum" element={<Impressum />} />
        <Route path="/agb" element={<AGB />} />
        <Route path="/widerrufsrecht" element={<Widerrufsrecht />} />
        <Route path="/datenschutz" element={<Datenschutz />} />
        <Route path="/warum-wir" element={<WarumWir />} />
        <Route path="/beispiele/landingpage" element={<LandingpageBeispiel />} />
        <Route path="/beispiele/business" element={<BusinessBeispiel />} />
        <Route path="/beispiele/online-shop" element={<OnlineShopBeispiel />} />
        <Route path="/beispiele/web-app" element={<WebAppBeispiel />} />
        <Route path="/ticket-system/login" element={<TicketLogin />} />
        <Route path="/ticket-system/reset-password" element={<TicketResetPassword />} />
        <Route path="/ticket-system" element={<ProtectedRoute><TicketDashboard /></ProtectedRoute>} />
        <Route path="/ticket-system/orders" element={<ProtectedRoute><TicketOrdersList /></ProtectedRoute>} />
        <Route path="/ticket-system/orders/new" element={<ProtectedRoute><TicketCreateOrder /></ProtectedRoute>} />
        <Route path="/ticket-system/orders/:id" element={<ProtectedRoute><TicketOrderDetail /></ProtectedRoute>} />
        <Route path="/ticket-system/team" element={<ProtectedRoute><TicketTeam /></ProtectedRoute>} />
        <Route path="/ticket-system/settings" element={<ProtectedRoute><TicketSettings /></ProtectedRoute>} />
        <Route path="/ticket-system/finanzen" element={<ProtectedRoute><TicketFinance /></ProtectedRoute>} />
        <Route path="/ticket-system/affiliates" element={<ProtectedRoute><AffiliatesAdmin /></ProtectedRoute>} />
        <Route path="/ticket-system/anrufliste" element={<ProtectedRoute><TicketCallList /></ProtectedRoute>} />
        <Route path="/ticket-system/subunternehmer" element={<ProtectedRoute><TicketSubcontractors /></ProtectedRoute>} />
        <Route path="/affiliate/login" element={<AffiliateLogin />} />
        <Route path="/affiliate" element={<AffiliateDashboard />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/beratungstermin" element={<BeratungsterminPage />} />
        <Route path="/blog/kosten-hausmeisterservice" element={<KostenHausmeisterservice />} />
        <Route path="/blog/:slug" element={<ServiceBlogPage />} />
        {localSeoPages.map((p) => (
          <Route key={p.slug} path={`/${p.slug}`} element={<LocalSeoPage page={p} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </AnimatePresence>
  );
};

const App = () => (
  <ThemeProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <BusinessModeProvider>
          <ScrollToTop />
          <RouteThemeSync />
          <AnimatedRoutes />
          <ConditionalCookieConsent />
          <ConditionalWhatsAppOrb />
        </BusinessModeProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;
